import { Flex, Form, Input, Modal } from 'antd';
import { Building2 } from 'lucide-react';
import { Store } from 'antd/es/form/interface';
import { useTranslation } from 'react-i18next';
import { AddIcon } from '@/components/Icons';
import { Heading } from '@/components/Heading';
import { toast } from '@/hooks/use-toast';
import { useCreateWorkspace } from '@/hooksApi';
import type { Workspace } from '@/types';
import WorkspaceTile from './WorkspaceTile';

const FIELD = 'border border-solid border-sd-input bg-sd-input-bg h-[40px] rounded-md w-full';

const NAME_MIN = 2;
const NAME_MAX = 60;

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Switch into the new workspace once it exists — creating one is a move. */
  onCreated?: (workspace: Workspace) => void | Promise<void>;
}

/**
 * BOXY-44 §4 — one field and a consequence, so a dialog rather than a page.
 *
 * Chrome, grid, field class and footer are `TeamMemberModal`/`TeamMemberForm`,
 * for the reason written at the top of `InviteMemberDialog`: the dashboard has
 * 33 AntD `Modal`s, and a shadcn `Dialog` among them reads as a different
 * product — different ground colour, different radius, a focus ring nothing
 * else in the app uses, and a scroll-lock that breaks any list inside it.
 *
 * ⭐ THE DESCRIPTION IS LOAD-BEARING, so it survived the conversion. A merchant
 * who thinks "new workspace" copies their catalogue, team and addresses will
 * create one and then file a bug. Saying what it holds, before the click, is
 * cheaper than every support conversation that follows not saying it. (The
 * invite dialog's paragraph was dropped on the owner's instruction — that one
 * explained a mechanism, this one prevents a wrong click.)
 *
 * One field only (addendum §9): everything else — pickup address, teammates,
 * first order — is post-create onboarding, where it can be done in context
 * rather than guessed at in a modal.
 */
const CreateWorkspaceDialog = ({ open, onOpenChange, onCreated }: Props) => {
  const { t } = useTranslation();
  const createWorkspace = useCreateWorkspace();
  const [form] = Form.useForm();

  // Drives only the initials tile. `Form.useWatch` rather than a second piece
  // of state: two sources for one value is how a preview starts disagreeing
  // with the field it previews.
  const name = (Form.useWatch('name', form) ?? '') as string;
  const trimmed = name.trim();

  const close = () => {
    form.resetFields();
    onOpenChange(false);
  };

  const onFinish = async (values: Store) => {
    try {
      const created = await createWorkspace.mutateAsync({ name: String(values.name).trim() });
      toast({ title: String(t('workspace.created')) });
      close();
      await onCreated?.(created.object);
    } catch {
      toast({ title: String(t('workspace.create_failed')), variant: 'destructive' });
    }
  };

  return (
    <Modal
      className='rounded-[20px] bg-white border border-customGrayShade3'
      title={
        <Flex style={{ borderBottom: '1px solid #E1E1E3', paddingBottom: '1rem' }}>
          <Heading
            title={String(t('workspace.new'))}
            upperCase
            fontSize='text-lg'
            fontWeight='font-semibold'
          />
        </Flex>
      }
      width={560}
      centered
      footer={null}
      open={open}
      onOk={close}
      onCancel={close}
      destroyOnClose
    >
      <Form form={form} name='workspace-create-form' className='workspace-form' onFinish={onFinish}>
        {/* Same rhythm as the invite modal and as `OrderForm/CustomerDetailsCard`:
            a small uppercase section label, then `mb-3` wrappers with the
            Form.Item at `mb-0`. Two dialogs a merchant reaches from the same
            page should not space themselves differently. */}
        <div className='mb-4'>
          <div className='mb-3 flex items-center gap-2'>
            <Building2 className='h-3.5 w-3.5 text-sd-muted-foreground' strokeWidth={1.5} />
            <span className='text-[10px] font-semibold uppercase tracking-wider text-sd-muted-foreground'>
              {String(t('workspace.name_label'))}
            </span>
          </div>

          <div className='mb-3'>
            <Form.Item
              name='name'
              className='mb-0'
              rules={[
                { required: true, message: t('this_field_is_required') },
                { min: NAME_MIN, message: t('this_field_is_required') },
              ]}
            >
              <Input
                maxLength={NAME_MAX}
                placeholder={String(t('workspace.name_placeholder'))}
                className={FIELD}
              />
            </Form.Item>
          </div>

          {/* The tile the merchant will recognise this store by, before they
              commit to the name. Cheaper to try another name here than to
              discover the initials collide with an existing store later. */}
          {trimmed.length > 0 ? (
            <Flex align='center' gap={8} className='text-[12px] text-customGrayShade5'>
              <WorkspaceTile uid={trimmed} name={trimmed} size='sm' />
              <span>{String(t('workspace.initials_preview'))}</span>
            </Flex>
          ) : null}
        </div>

        <Flex className='mt-8' justify='end' gap={16}>
          <button
            type='button'
            onClick={close}
            className='flex items-center gap-1.5 h-9 px-4 rounded-lg border border-solid border-[rgb(232,230,227)] bg-white text-[rgb(57,57,60)] text-[13px] font-semibold cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[rgb(245,245,243)] transition-colors uppercase'
          >
            {t('cancel')}
          </button>
          <button
            type='submit'
            disabled={createWorkspace.isLoading}
            className='flex items-center gap-1.5 h-9 px-5 rounded-lg bg-[rgb(237,224,74)] text-[rgb(57,57,60)] text-[13px] font-semibold cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[rgb(230,217,60)] transition-colors uppercase'
          >
            <AddIcon width={20} height={20} />
            {createWorkspace.isLoading ? t('loading') : t('workspace.create')}
          </button>
        </Flex>
      </Form>
    </Modal>
  );
};

export default CreateWorkspaceDialog;
